import React, { useState, useCallback, useEffect, useMemo } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { partial, isUndefined } from 'underscore';

import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import {
  SmartTextEditor,
  SmartRichTextEditor,
  TextErrorMap,
} from '@bingads-webui-campaign-react/smart-component-text';
import {
  useEditorStatusContext,
  useMobileSupportEnabled,
} from '@bingads-webui-campaign-react/smart-page-context';
import {
  useDefaultEditorStyle,
  DATA_SOURCE_KEY_MAPPING,
  componentsConfig,
} from '@bingads-webui-campaign-react/smart-component-util';
import { SmartHeaderEditor } from '@bingads-webui-campaign-react/smart-component-header';

import { dataProperties, headlineMaxCount, descriptionMaxCount } from './data-config';
import { useSmartIntroductionData } from './smart-introduction-data';

const getStyles = () => ({
  label: {
    fontSize: '16px',
  },
  sectionContainer: {
    margin: '10px 0',
  },
});

const descriptionTextError = {
  [TextErrorMap.MaxLength]: _TL_('This field can\'t exceed {{maxCharCount}} characters. Select the Business summary component below to describe your business in more detail.'),
};

export const UnStyledSmartIntroductionEditor = ({
  classes,
  i18n,
  activeDataProperty,
  componentId,
}) => {
  const {
    titleClassName,
    descriptionClassName,
    editorContentClassName,
  } = useDefaultEditorStyle();

  const [highlightField, setHighlightField] = useState(activeDataProperty);
  const isMobile = useMobileSupportEnabled();
  const [{ selectedComponentType }] = useEditorStatusContext();
  const {
    headline,
    defaultHeadline,
    description,
    defaultDescription,
    update,
  } = useSmartIntroductionData(componentId);

  const onChange = useCallback((property, { value }) => update(property, value), [update]);

  useEffect(() => setHighlightField(null), []);

  const headerSection = useMemo(() => (
    <div className={classNames(classes.sectionContainer, 'header-section')}>
      <SmartHeaderEditor
        i18n={i18n}
        highlightField={highlightField}
        componentId={componentId}
      />
    </div>
  ), [classes, componentId, highlightField, i18n]);

  const descriptionSection = useMemo(() => (
    <div className={classNames(classes.sectionContainer, 'description-section')}>
      {
        !isMobile
        && <h3 className={classes.label}>{i18n.getString(_TL_('Description'))}</h3>
      }
      <div data-editable-property={dataProperties.headline} >
        <SmartTextEditor
          label={i18n.getString(_TL_('Headline'))}
          focus={highlightField === dataProperties.headline}
          onChange={partial(onChange, dataProperties.headline)}
          maxCharCount={headlineMaxCount}
          placeholder={defaultHeadline}
          value={headline}
        />
      </div>
      <div data-editable-property={dataProperties.description} >
        <SmartRichTextEditor
          className="introduction-short-description-rich-text-editor"
          label={i18n.getString(_TL_('Short description'))}
          onChange={partial(onChange, dataProperties.description)}
          maxCharCount={descriptionMaxCount}
          dataSourceName={DATA_SOURCE_KEY_MAPPING.BusinessDescription}
          placeholder={isUndefined(description) ? defaultDescription : ''}
          value={description}
          customErrorMap={descriptionTextError}
        />
      </div>
    </div>
  ), [
    classes, defaultDescription, defaultHeadline, isMobile,
    description, headline, highlightField, i18n, onChange,
  ]);

  const editorConfigs = useMemo(() => {
    if (isMobile) {
      if (selectedComponentType === componentsConfig.header.type) {
        return {
          editorContent: headerSection,
          title: _TL_('Logo and business name'),
          description: _TL_('Choose your logo and confirm your business name.'),
        };
      }

      // type === introduction
      return {
        editorContent: descriptionSection,
        title: _TL_('Headline and business description'),
        description: _TL_('Add the following required information to introduce your business to your customers.'),
      };
    }

    return {
      editorContent: [headerSection, descriptionSection],
      title: _TL_('Business description'),
      description: _TL_('Add all the required information in this section to introduce your business to your customers.'),
    };
  }, [descriptionSection, headerSection, isMobile, selectedComponentType]);

  return (
    <div>
      <h2 className={titleClassName}>
        {i18n.getString(editorConfigs.title)}
      </h2>
      <div className={descriptionClassName}>
        {i18n.getString(editorConfigs.description)}
      </div>
      <div className={editorContentClassName}>
        {editorConfigs.editorContent}
      </div>
    </div>
  );
};

UnStyledSmartIntroductionEditor.propTypes = {
  classes: PropTypes.shape({}).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  activeDataProperty: PropTypes.string,
  componentId: PropTypes.string.isRequired,
};

UnStyledSmartIntroductionEditor.defaultProps = {
  activeDataProperty: null,
};

export const SmartIntroductionEditor = withDefaultStyles(
  UnStyledSmartIntroductionEditor,
  getStyles
);
